<template>
  <nav
    class="wws-stepper"
    role="progressbar"
    :aria-label="`Step ${activeStep + 1} of ${steps}`"
    :aria-valuemin="1"
    :aria-valuemax="steps"
    :aria-valuenow="activeStep + 1"
    data-testid="welcome-wizard-stepper"
  >
    <span
      v-for="index in steps"
      :key="index"
      class="wws-segment"
      :class="{
        'wws-segment--filled': index - 1 <= activeStep,
        'wws-segment--current': index - 1 === activeStep,
      }"
      :aria-hidden="true"
      :data-testid="`wws-segment-${index - 1}`"
    ></span>
  </nav>
</template>

<script setup>
import { computed } from "vue";

/**
 * ER-12245 — WelcomeWizardStepper (MDP-621).
 *
 * One horizontal segment per wizard page the user will see. The segment count is
 * `totalSteps` from the API (never derived in the frontend), the current page is
 * filled, and every earlier page stays filled. Presentational only: it never
 * decides the count or the position, and it never navigates.
 */
const props = defineProps({
  /** Number of pages the user will see (segment count). */
  totalSteps: {
    type: Number,
    default: 1,
    validator: (value) => Number.isInteger(value) && value >= 1,
  },
  /** 0-based index of the current page (filled). */
  currentStep: {
    type: Number,
    default: 0,
    validator: (value) => Number.isInteger(value) && value >= 0,
  },
});

// Clamp defensively so a stale currentStep from the API never renders a broken bar.
const steps = computed(() => Math.max(1, props.totalSteps));
const activeStep = computed(() => Math.min(Math.max(0, props.currentStep), steps.value - 1));
</script>

<style scoped>
.wws-stepper {
  display: flex;
  gap: 8px;
  width: 232px;
  max-width: 100%;
}

.wws-segment {
  flex: 1 1 0;
  height: 4px;
  background: #eaecf0;
  border-radius: 2px;
}

.wws-segment--filled,
.wws-segment--current {
  background: #101828;
}
</style>
